import moment from 'moment';

import type { CostWindow } from '../types/optimise';

/**
 * Battery command types from the API
 */
export type BatteryCommand = 'charge' | 'discharge' | 'hold';

/**
 * Rate type categories
 */
export type RateType = 'cheap' | 'day' | 'peak';

/**
 * Raw behaviour forecast record from the API
 */
export interface BehaviourForecastRecord {
  target_datetime_utc: number; // Unix timestamp in seconds
  optimal_target_soc_original: number; // Target SOC percentage
  battery_update_command?: BatteryCommand;
}

/**
 * Enriched forecast point with command and tariff information
 */
export interface EnrichedForecastPoint {
  x: string; // Time (HH:mm)
  y: number; // Target SOC percentage
  data: BehaviourForecastRecord;
  command: BatteryCommand;
  rateType: RateType;
  rate: number; // Rate in p/kWh
  startTime: string; // Command start time (HH:mm)
  endTime: string; // Command end time - next point's time (HH:mm)
}

/**
 * Formats a UTC Unix timestamp to local HH:mm format
 *
 * @param timestampUtc - Unix timestamp in seconds (UTC)
 * @returns Formatted time string in HH:mm format
 */
export const formatTimeFromUtcTimestamp = (timestampUtc: number): string => {
  return moment
    .utc(timestampUtc * 1000)
    .local()
    .format('HH:mm');
};

/**
 * Finds the applicable rate for a given timestamp from cost windows
 *
 * @param timestampUtc - Unix timestamp in seconds (UTC)
 * @param costWindows - Array of cost windows from the tariff API
 * @returns The matching cost window or undefined if not found
 */
export const findRateForTimestamp = (timestampUtc: number, costWindows: CostWindow[]): CostWindow | undefined => {
  if (!costWindows?.length) return undefined;

  // Sort cost windows by start timestamp (ascending)
  const sortedWindows = [...costWindows].sort((a, b) => a.start_timestamp_utc - b.start_timestamp_utc);

  // Find the cost window that contains this timestamp
  // The timestamp falls within a window if it's >= window start and < next window start
  for (let i = 0; i < sortedWindows.length; i++) {
    const currentWindow = sortedWindows[i];
    const nextWindow = sortedWindows[i + 1];

    if (nextWindow) {
      // If there's a next window, check if timestamp is within current window
      if (timestampUtc >= currentWindow.start_timestamp_utc && timestampUtc < nextWindow.start_timestamp_utc) {
        return currentWindow;
      }
    } else {
      // Last window - check if timestamp is after window start
      if (timestampUtc >= currentWindow.start_timestamp_utc) {
        return currentWindow;
      }
    }
  }

  return undefined;
};

/**
 * Categorises a rate value into cheap, day, or peak based on the rate value
 * relative to other rates in the cost windows
 *
 * @param rate - The rate value in p/kWh
 * @param costWindows - Array of cost windows to derive thresholds from
 * @returns The rate type category
 */
export const categoriseRate = (rate: number, costWindows: CostWindow[]): RateType => {
  if (!costWindows?.length) return 'day';

  // Get unique rates and sort them
  const uniqueRates = [...new Set(costWindows.map((w) => w.rate))].sort((a, b) => a - b);

  if (uniqueRates.length === 1) {
    // Only one rate - consider it day rate
    return 'day';
  } else if (uniqueRates.length === 2) {
    // Two rates - lowest is cheap, highest is day
    return rate === uniqueRates[0] ? 'cheap' : 'day';
  } else {
    // Three or more rates - lowest is cheap, highest is peak, rest are day
    if (rate === uniqueRates[0]) return 'cheap';
    if (rate === uniqueRates[uniqueRates.length - 1]) return 'peak';
    return 'day';
  }
};

/**
 * Processes behaviour forecast records into enriched chart data points
 * with command and tariff information
 *
 * @param forecast - Array of behaviour forecast records from the API
 * @param costWindows - Array of cost windows from the tariff API (optional)
 * @returns Array of enriched forecast points for chart rendering
 */
export const processForecastForChart = (
  forecast: BehaviourForecastRecord[],
  costWindows: CostWindow[] = []
): EnrichedForecastPoint[] => {
  if (!forecast?.length) return [];

  return forecast.map((record, index) => {
    const nextRecord = forecast[index + 1];
    const rateWindow = findRateForTimestamp(record.target_datetime_utc, costWindows);
    const rate = rateWindow?.rate ?? 0;
    const rateType = rateWindow?.rate_type ?? categoriseRate(rate, costWindows);

    return {
      x: formatTimeFromUtcTimestamp(record.target_datetime_utc),
      y: Math.min(record.optimal_target_soc_original ?? 0, 100),
      data: record,
      command: record.battery_update_command ?? 'hold',
      rateType,
      rate,
      startTime: formatTimeFromUtcTimestamp(record.target_datetime_utc),
      endTime: nextRecord ? formatTimeFromUtcTimestamp(nextRecord.target_datetime_utc) : '',
    };
  });
};

/**
 * Generates human-readable tooltip content for a forecast point
 *
 * Format follows AC requirements:
 * - Hold: "Hold at 19% from 21:30 to 22:00"
 * - Charge: "Charge from 22:00 to 22:30 to target 62%"
 * - Discharge: "Discharge from 17:00 to 17:30 to 30%"
 *
 * @param point - The enriched forecast point
 * @returns Formatted tooltip text with command details and tariff info
 */
export const generateTooltipContent = (point: EnrichedForecastPoint): string => {
  const rateLabels: Record<RateType, string> = {
    cheap: 'Cheap rate',
    day: 'Day rate',
    peak: 'Peak rate',
  };

  const rateLabel = rateLabels[point.rateType] || 'Day rate';

  // Format the rate to 1 decimal place
  const rateValue = point.rate > 0 ? `${point.rate.toFixed(1)}p/kWh` : '';
  const rateInfo = rateValue ? `\n${rateLabel}: ${rateValue}` : '';

  // Build tooltip based on command type with AC-specified format
  if (point.command === 'hold') {
    // Format: "Hold at 19% from 21:30 to 22:00"
    const timeRange = point.endTime ? `${point.startTime} to ${point.endTime}` : `from ${point.startTime}`;
    return `Hold at ${Math.round(point.y)}%\n${timeRange}${rateInfo}`;
  } else if (point.command === 'charge') {
    // Format: "Charge from 22:00 to 22:30 to target 62%"
    const timeRange = point.endTime ? `${point.startTime} to ${point.endTime}` : `from ${point.startTime}`;
    return `Charge to target ${Math.round(point.y)}%\n${timeRange}${rateInfo}`;
  } else {
    // Discharge format: "Discharge from 17:00 to 17:30 to 30%"
    const timeRange = point.endTime ? `${point.startTime} to ${point.endTime}` : `from ${point.startTime}`;
    return `Discharge to ${Math.round(point.y)}%\n${timeRange}${rateInfo}`;
  }
};

/**
 * Identifies transition points where the command changes
 * These are the points where visual markers should be displayed
 *
 * @param points - Array of enriched forecast points
 * @returns Array of points where command transitions occur
 */
export const findCommandTransitions = (points: EnrichedForecastPoint[]): EnrichedForecastPoint[] => {
  if (!points?.length || points.length < 2) return [];

  const transitions: EnrichedForecastPoint[] = [];

  for (let i = 1; i < points.length; i++) {
    const prevPoint = points[i - 1];
    const currentPoint = points[i];

    // Check if command changed
    if (prevPoint.command !== currentPoint.command) {
      transitions.push(currentPoint);
    }
  }

  return transitions;
};

/**
 * Gets the colour for a command type (for visual markers)
 *
 * @param command - The battery command type
 * @returns Colour string for the command
 */
export const getCommandColour = (command: BatteryCommand): string => {
  const colours: Record<BatteryCommand, string> = {
    charge: '#4CAF50', // Green
    discharge: '#FF9800', // Orange
    hold: '#9E9E9E', // Grey
  };

  return colours[command] || colours.hold;
};
